import { useTranslation } from "react-i18next";
// src/guards/SetupGuard.tsx (or wherever this lives)
import React, { ReactNode, useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { apiGet } from "../api/client";
import { useParams } from "react-router-dom";
import { companyRoute } from "../utils/routeHelpers";
import { AuthMe } from "../types/auth"; // <-- use the shared type
import { A } from "../styles/appleClasses";
import { hasUpgradeIntent, upgradeIntentSearch } from "../utils/upgradeIntent";

export function SetupGuard({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [setupComplete, setSetupComplete] = useState<boolean | null>(null);
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();
  const isWelcome = location.pathname.endsWith("/welcome");

  useEffect(() => {
    let cancelled = false;

    async function checkSetup() {
      try {
        // pull the canonical shape we get from /auth/me
        const user = await apiGet<AuthMe>("/auth/me");

        if (!cancelled) {
          setSetupComplete(!!user.setup_complete);
        }
      } catch (e) {
        // If /auth/me fails (not logged in, token expired, etc.)
        if (!cancelled) {
          setSetupComplete(false);
        }
      }
    }

    checkSetup();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Still loading? Show the spinner/placeholder
  if (setupComplete === null) {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center`}>
        <div className={`${A.label}`}>{t("purchases.edit.loading")}</div>
      </div>
    );
  }

  // User has NOT completed setup, and they're trying to hit a normal app page.
  // Kick them to the welcome/onboarding path (company-aware).
  if (!setupComplete && !isWelcome) {
    return <Navigate to={`${companyRoute(company_id, "/welcome")}${upgradeIntentSearch(location.search)}`} replace />;
  }

  // User HAS completed setup, but they're on /welcome already.
  // Send them "home" so they don't see onboarding again.
  if (setupComplete && isWelcome) {
    return <Navigate to={hasUpgradeIntent(location.search) ? `${companyRoute(company_id, "/settings")}?upgrade=1` : companyRoute(company_id, "/")} replace />;
  }

  // Otherwise render the requested route.
  return <>{children}</>;
}
